import { useState } from "react";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { useParams } from "react-router-dom";
import { BorderlessButton } from "../../components/BorderlessButton";
import { StickyTable } from "../../components/StickyTable";
import { dateFormat } from "../../util/format";
import { useAccount, usePrefetchShowSettings, useUnassignUser, useUsers } from "../account-api";
import { usePermissions } from "../current-user-api";
import { UserModal } from "./UserModal";

export function AssignedUserTable() {
    const { accountID } = useParams();
    usePrefetchShowSettings(accountID);
    const { data: account } = useAccount(accountID);
    const { data: permissions } = usePermissions(accountID);
    const { data: users } = useUsers(accountID);
    const unassign = useUnassignUser(accountID);
    const [editingUser, setEditingUser] = useState();
    const noMoreUsers = account?.maxUsers <= users?.length;

    return (
        <>
            <StickyTable>
                <thead>
                    <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Access</th>
                        <th>Last sign-in</th>
                        <th>Edit</th>
                        <th>Remove</th>
                    </tr>
                </thead>
                <tbody>
                    {users?.map(u => (
                        <tr key={u.userID}>
                            <td style={{ height: "3em" }}>{u.name}</td>
                            <td>{u.email}</td>
                            <td>
                                <AccessDisplay user={u} />
                            </td>
                            <td style={{ textAlign: "right" }}>
                                {u.lastLoginDate ? dateFormat(u.lastLoginDate) : "pending"}
                            </td>
                            <td>
                                <BorderlessButton
                                    disabled={!u.canEdit || !permissions?.canEditUsers}
                                    onClick={() => setEditingUser(u)}
                                >
                                    <FaEdit />
                                </BorderlessButton>
                            </td>
                            <td>
                                <BorderlessButton
                                    disabled={!u.canUnassign || !permissions?.canEditUsers}
                                    onClick={() => {
                                        if (confirm(`Remove ${u.email} from this account?`)) {
                                            unassign.mutateAsync(u.userID);
                                        }
                                    }}
                                >
                                    <FaTrashAlt />
                                </BorderlessButton>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </StickyTable>
            {permissions?.canEditUsers ? (
                <div style={{ display: "flex", gap: "1em", alignItems: "center" }}>
                    <button
                        disabled={noMoreUsers}
                        onClick={() =>
                            setEditingUser({
                                accountID,
                                name: "",
                                email: "",
                                accountPermission: ""
                            })
                        }
                    >
                        Invite User
                    </button>
                    {noMoreUsers ? (
                        <small>
                            This account has reached it's maximum user limit of {account.maxUsers}
                        </small>
                    ) : null}
                </div>
            ) : null}

            <UserModal user={editingUser} close={() => setEditingUser(undefined)} />
        </>
    );
}

function AccessDisplay({ user }) {
    const accountAdmin = user.accountPermission == "Admin";
    const accountViewer = user.accountPermission == "Viewer";
    const showPerms = [
        user.showAdminCount ? `Admin (${user.showAdminCount})` : undefined,
        user.showViewerCount ? `Viewer (${user.showViewerCount})` : undefined
    ]
        .filter(Boolean)
        .join(" / ");
    return accountAdmin
        ? "Account Admin"
        : accountViewer
        ? "Account Viewer"
        : user.showCount
        ? `Show ${showPerms}`
        : "None";
}
